import { Grid } from '@mui/joy'
import { useMutation } from '@tanstack/react-query'
import { UseFormReturn } from 'react-hook-form'
import { Penalty, PenaltyCreateFormValues } from '~/shared/api'
import { logger } from '~/shared/lib/logger'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { Button } from '~/shared/ui/Buttons'
import { TextInput } from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'

type OdpsRecipientProps = {
  form: UseFormReturn<PenaltyCreateFormValues>
}

/*
 * Реквизиты получателя платежа.
 *
 * Их не ведут справочником: они приходят вместе с начислением и зависят от
 * подразделения ГАИ. Поэтому по коду подразделения подставляются реквизиты
 * из последнего загруженного штрафа того же подразделения, а оператор при
 * необходимости правит их по постановлению.
 * */
export function OdpsRecipient({ form }: OdpsRecipientProps) {
  const odpsCode = form.watch('odpsCode')

  const { mutate: fillRecipient, isLoading } = useMutation({
    mutationFn: (code: string) => Penalty.getRecipientByOdps(code),
    onSuccess(recipient) {
      form.setValue('odpsName', recipient.odpsName ?? '')
      form.setValue('odpsAddr', recipient.odpsAddr ?? '')
      form.setValue('recipient', recipient.recipient ?? '', {
        shouldValidate: true,
      })
      form.setValue('recipientInn', recipient.recipientInn ?? '', {
        shouldValidate: true,
      })
      form.setValue('recipientKpp', recipient.recipientKpp ?? '', {
        shouldValidate: true,
      })
      form.setValue(
        'recipientBankAccountNumber',
        recipient.recipientBankAccountNumber ?? '',
        { shouldValidate: true },
      )
      form.setValue(
        'recipientBankCorrAccountNumber',
        recipient.recipientBankCorrAccountNumber ?? '',
        { shouldValidate: true },
      )
      form.setValue('recipientBankName', recipient.recipientBankName ?? '', {
        shouldValidate: true,
      })
      form.setValue('recipientBankBik', recipient.recipientBankBik ?? '', {
        shouldValidate: true,
      })
      form.setValue(
        'recipientBankUsername',
        recipient.recipientBankUsername ?? '',
        { shouldValidate: true },
      )
      form.setValue('recipientBankKbk', recipient.recipientBankKbk ?? '', {
        shouldValidate: true,
      })
      form.setValue('recipientBankOktmo', recipient.recipientBankOktmo ?? '', {
        shouldValidate: true,
      })

      snackbarEnqueued({
        message: 'Реквизиты подставлены, проверьте их по постановлению',
        variant: 'success',
      })
    },
    onError(error) {
      logger.error(error)
      snackbarEnqueued({
        message: 'Реквизиты по указанному подразделению не найдены',
        variant: 'warning',
      })
    },
  })

  return (
    <Container>
      <ContainerTitle>Получатель платежа</ContainerTitle>
      <Grid container spacing={2.5}>
        <Grid xs={3}>
          <TextInput
            label='Код подразделения ГАИ'
            name='odpsCode'
            placeholder='1107067'
          />
        </Grid>
        <Grid xs={3} sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button
            variant='gray'
            disabled={!odpsCode}
            loading={isLoading}
            onClick={() => odpsCode && fillRecipient(odpsCode)}
            data-testid='fill-recipient-button'
            sx={{ marginBottom: '4px' }}
          >
            Подставить реквизиты
          </Button>
        </Grid>
        <Grid xs={6}>
          <TextInput label='Наименование подразделения' name='odpsName' />
        </Grid>

        <Grid xs={12}>
          <TextInput label='Администратор начисления' name='recipient' />
        </Grid>
        <Grid xs={12}>
          <TextInput
            label='Наименование получателя платежа'
            name='recipientBankUsername'
          />
        </Grid>
        <Grid xs={3}>
          <TextInput label='ИНН получателя' name='recipientInn' />
        </Grid>
        <Grid xs={3}>
          <TextInput label='КПП получателя' name='recipientKpp' />
        </Grid>
        <Grid xs={6}>
          <TextInput label='Расчётный счёт' name='recipientBankAccountNumber' />
        </Grid>
        <Grid xs={6}>
          <TextInput label='Банк получателя' name='recipientBankName' />
        </Grid>
        <Grid xs={3}>
          <TextInput label='БИК' name='recipientBankBik' />
        </Grid>
        <Grid xs={3}>
          <TextInput
            label='Корреспондентский счёт'
            name='recipientBankCorrAccountNumber'
          />
        </Grid>
        <Grid xs={3}>
          <TextInput label='КБК' name='recipientBankKbk' />
        </Grid>
        <Grid xs={3}>
          <TextInput label='ОКТМО' name='recipientBankOktmo' />
        </Grid>
        <Grid xs={6}>
          <TextInput label='Назначение платежа' name='recipientBankPurpose' />
        </Grid>
      </Grid>
    </Container>
  )
}
